// ABAY360 i18n bootstrap — global multi-language framework (Phase 1).
// Supports English (default), Amharic, Afaan Oromo, Tigrinya and Somali.
// Non-English strings are AI-generated first drafts pending native review.
// English is the fallback for any key that is not yet translated, so the UI
// never breaks. Selection is persisted in localStorage ('abay-lang').
import i18n from "i18next";
import { initReactI18next, useTranslation } from "react-i18next";

import en from "@/i18n/locales/en.json";
import am from "@/i18n/locales/am.json";
import om from "@/i18n/locales/om.json";
import ti from "@/i18n/locales/ti.json";
import so from "@/i18n/locales/so.json";

// Public, extensible language registry. Add a new object here + a locale file
// to support another language.
export const SUPPORTED_LANGUAGES = [
  { code: "en", label: "EN", native: "English" },
  { code: "am", label: "አማ", native: "አማርኛ", aiName: "Amharic (አማርኛ)" },
  { code: "om", label: "OR", native: "Afaan Oromoo", aiName: "Afaan Oromo" },
  { code: "ti", label: "ትግ", native: "ትግርኛ", aiName: "Tigrinya (ትግርኛ)" },
  { code: "so", label: "SO", native: "Soomaali", aiName: "Somali (Soomaali)" },
];

export const STORAGE_KEY = "abay-lang";

const isSupported = (code) => SUPPORTED_LANGUAGES.some((l) => l.code === code);

const getStoredLang = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return isSupported(v) ? v : "en";
  } catch (e) {
    return "en";
  }
};

i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    am: { translation: am },
    om: { translation: om },
    ti: { translation: ti },
    so: { translation: so },
  },
  lng: getStoredLang(),
  fallbackLng: "en",
  supportedLngs: SUPPORTED_LANGUAGES.map((l) => l.code),
  interpolation: { escapeValue: false },
  returnEmptyString: false,
  react: { useSuspense: false },
});

const applyDocLang = (lng) => {
  try { document.documentElement.lang = lng; } catch (e) { /* noop */ }
};
applyDocLang(i18n.language);

i18n.on("languageChanged", (lng) => {
  try { localStorage.setItem(STORAGE_KEY, lng); } catch (e) { /* quota */ }
  applyDocLang(lng);
});

// Returns the AI directive name for the current language (null for English).
export const getAiLanguageName = () => {
  const l = SUPPORTED_LANGUAGES.find((x) => x.code === i18n.language);
  return l && l.aiName ? l.aiName : null;
};

// Convenience hook used by the language selector.
export const useLanguage = () => {
  const { i18n: instance } = useTranslation();
  const current =
    SUPPORTED_LANGUAGES.find((l) => l.code === instance.language) || SUPPORTED_LANGUAGES[0];
  const setLanguage = (code) => { if (isSupported(code)) instance.changeLanguage(code); };
  return { current, setLanguage, languages: SUPPORTED_LANGUAGES };
};

export default i18n;
